/**
 * @file Controller for the on-screen keypad
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2404.20.001
 */

/** @module gamepad */

class GameTouchControls {


	/** 
	 * The `Game` instance.
	 * @type {module:game~Game?}
	 */
	#game = null;

	/** 
	 * The player choice container.
	 * @type {HTMLElement?}
	 */
	#playerChoiceContainer = null;

	/**	
	 * The `INTERACT` button 
	 * @type {HTMLElement?}
	 */
	#interactButton = null;

	/**
	 * Fire the `preventDefault` method of an event, if it exists.
	 * @param {Event} event 
	 */
	#preventDefault( event ) {
		event && event.preventDefault && event.preventDefault();
	}

	/**
	 * Disable/enable the interaction button, based on the game's interaction 
	 * events.
	 * @param {Event} interactionEvent 
	 */
	#onGameInteraction( interactionEvent ) {
		this.#interactButton.disabled = (
			interactionEvent.type === "interactionleave"
		);
	}

	/**
	 * Determine the control key associated to a button element.
	 * @param   {HTMLElement} element 
	 * @returns {String?} UP | DOWN | LEFT | RIGHT | INTERACT | null
	 */	 
	#getButtonKey( element ) {
		let target = element;
		while( target && target.tagName !== "BUTTON" ) {
			target = target.parentElement;
		}
		if ( !target ) { return null; }
		let key = null;
		const classList = target.classList;
		if ( classList.contains( "up" ) ) { key = "UP"; }
		else if ( classList.contains( "down" ) ) { key = "DOWN"; }
		else if ( classList.contains( "left" ) ) { key = "LEFT"; }
		else if ( classList.contains( "right" ) ) { key = "RIGHT"; }
		else if ( classList.contains( "interact" ) ) { key = "INTERACT"; }
		return key;
	}

	/**
	 * Handle the `touchstart` and `touchend` events for a button.
	 * @param {Event} touchEvent 
	 */
	#onButtonTouchStartEnd( touchEvent ) {

		// Classify the event as a "down" or "up", ressembling a `key` event.
		let eventType = "";
		if ( touchEvent.type === "touchstart" ) { eventType = "down"; }
		else if ( touchEvent.type === "touchend" ) { eventType = "up"; }
		if ( !eventType ) { return; }

		// Determine the control key associated to the button.
		const controlKey = this.#getButtonKey( touchEvent.target );
		if ( !controlKey ) { return; }

		// If the player selector is not visible, fire the event and exit.
		if ( !this.#game.playerSelectorIsVisible ) {
			this.#game.fireKeyEvent( eventType, controlKey );
			return;
		}

		// Change the player selection, on "RIGHT" or "LEFT" button press.
		const hasPressedRight = ( controlKey === "RIGHT" );
		const hasPressedLeft = ( controlKey === "LEFT" );
		if ( !hasPressedRight && !hasPressedLeft ) { return; }

		const selectedCharacter = this.#playerChoiceContainer.querySelector( 
			"input:checked" 
		);
		// Get the selected (current) character container.
		let parentListItem = selectedCharacter.parentElement;
		while( parentListItem && parentListItem.tagName !== "LI" ) {
			parentListItem = parentListItem.parentElement;
		}
		if ( !parentListItem ) { return; }

		// Determine the next character container
		const goToListItem = hasPressedRight
			? parentListItem.nextElementSibling
			: parentListItem.previousElementSibling
		;
		if ( !goToListItem ) { return; }

		// Change the character selection input
		const goToListItemInput = goToListItem.querySelector( "input" );
		if ( !goToListItemInput ) { return; }
		selectedCharacter.checked = false;
		goToListItemInput.checked = true;

		goToListItemInput.focus();
		goToListItemInput.click();

	}

	/**
	 * An object that handles a the touch controls for a `game`.
	 * @param {module:game~Game} game 
	 * @param {HTMLElement} container 
	 * @param {HTMLElement} playerChoiceContainer
	 */
	constructor( game, container, playerChoiceContainer ) {

		this.#game = game;
		this.#interactButton = container.querySelector( "button.interact" );
		this.#interactButton.disabled = true;
		this.#playerChoiceContainer = playerChoiceContainer;

		const buttons = container.querySelectorAll( "button" );
		const onTouchStartEnd = this.#onButtonTouchStartEnd.bind( this );
		for( let b = buttons.length - 1; b >= 0; b = b - 1 ) {
			const button = buttons[ b ];
			button.addEventListener( "click", this.#preventDefault );
			button.addEventListener( "touchstart", onTouchStartEnd );
			button.addEventListener( "touchend", onTouchStartEnd );
			button.addEventListener( "touchmove", this.#preventDefault );
		}

		// Update the interact button state, based on the game's pertinent events.
		const onInteraction = this.#onGameInteraction.bind( this );
		game.canvas.addEventListener( "interactionenter", onInteraction );
		game.canvas.addEventListener( "interactionleave", onInteraction );

	}

}

export { GameTouchControls }